/**
 * PhaseOverview – Phase page with progress bar, category filter badges,
 * and step cards grouped into "Completed" / "Next Up" sections.
 * Figma Frame 41 (876:6060).
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressHeader from './ProgressHeader';
import StepCard from './StepCard';
import type { SettlementStep } from '../../utils/settlementFlowConfig';
import { SETTLEMENT_PLANNER_LAYOUT } from './settlementTheme';

type StepStatus = 'not-started' | 'in-progress' | 'complete';

export interface PhaseStepState {
  step: SettlementStep;
  status: StepStatus;
  subTaskProgress?: { completed: number; total: number };
}

interface PhaseOverviewProps {
  title: string;
  subtitle?: string;
  /** Short label above the title (Figma Frame 272) */
  eyebrow?: string;
  /**
   * Section ordering for step lists.
   * `next-first` matches Figma Frame 272: upcoming work before completed history.
   */
  sectionOrder?: 'completed-first' | 'next-first';
  /** When true and steps define `sectionGroup`, render steps under section headings */
  useStepSectionGroups?: boolean;
  /** Optional checklist-level progress totals to show in the main progress card */
  phaseChecklist?: { label: string; completed: number; total: number };
  /** Optional override for the active-work section label. */
  activeSectionLabel?: string;
  /** Optional layout variant for page-level visual alignment. */
  layoutVariant?: 'default' | 'planner';
  steps: PhaseStepState[];
  estimatedMinutes?: number;
  onStepClick: (stepId: string) => void;
  onCategoryClick?: (category: string) => void;
  /** Array of active categories to filter the visible cards */
  activeFilters?: string[];
  /** Progress summary noun (default tasks); use steps on phase dashboards */
  progressCompletedItemLabel?: string;
  /** Optional content to render between header/progress and workflow steps (e.g. notification tracker) */
  notificationTrackerContent?: React.ReactNode;
  /** Optional intro/supporting copy to show under the subtitle */
  intro?: string;
}

function sortIncompleteFirst(items: PhaseStepState[]): PhaseStepState[] {
  return [...items].sort((a, b) => {
    const ca = a.status === 'complete' ? 1 : 0;
    const cb = b.status === 'complete' ? 1 : 0;
    if (ca !== cb) return ca - cb;
    return a.step.stepNumber - b.step.stepNumber;
  });
}

export default function PhaseOverview({
  title,
  subtitle,
  intro,
  eyebrow,
  sectionOrder = 'completed-first',
  useStepSectionGroups,
  phaseChecklist,
  activeSectionLabel,
  layoutVariant = 'default',
  steps,
  estimatedMinutes,
  onStepClick,
  onCategoryClick,
  activeFilters = [],
  progressCompletedItemLabel = 'tasks',
  notificationTrackerContent,
}: PhaseOverviewProps) {
  const navigate = useNavigate();
  const completedSteps = steps.filter((s) => s.status === 'complete').length;
  const totalSteps = steps.length;
  const displayCompletedSteps = phaseChecklist?.total ? phaseChecklist.completed : completedSteps;
  const displayTotalSteps = phaseChecklist?.total ? phaseChecklist.total : totalSteps;
  const displayCompletedItemLabel = phaseChecklist?.total ? 'checklist items' : progressCompletedItemLabel;

  const completedItems = steps.filter((s) => s.status === 'complete');
  const inProgressItems = steps.filter((s) => s.status === 'in-progress');
  const upNextOnlyItems = steps.filter((s) => s.status === 'not-started');
  const nextUpItems = steps.filter((s) => s.status !== 'complete');

  const isPlannerLayout = layoutVariant === 'planner';
  const usePlannerTripleSections =
    isPlannerLayout && sectionOrder === 'next-first' && !useStepSectionGroups;

  const sectionGroupedSteps = useMemo(() => {
    const enabled = !!useStepSectionGroups && steps.some((s) => !!s.step.sectionGroup);
    if (!enabled) return null;
    const order: string[] = [];
    const map = new Map<string, PhaseStepState[]>();
    for (const s of steps) {
      const label = (s.step.sectionGroup || 'Other').trim();
      if (!map.has(label)) {
        order.push(label);
        map.set(label, []);
      }
      map.get(label)!.push(s);
    }
    return order.map((label) => ({ label, steps: sortIncompleteFirst(map.get(label)!) }));
  }, [steps, useStepSectionGroups]);

  // Collect unique categories for filter badges
  const categories = useMemo(() => {
    const cats = new Set<string>();
    steps.forEach((s) => {
      if (s.step.category) cats.add(s.step.category);
    });
    return Array.from(cats);
  }, [steps]);

  const containerClassName = isPlannerLayout
    ? SETTLEMENT_PLANNER_LAYOUT.container
    : 'max-w-3xl mx-auto';
  const contentColumnClassName = isPlannerLayout ? SETTLEMENT_PLANNER_LAYOUT.contentColumn : '';
  const titleClassName = isPlannerLayout
    ? SETTLEMENT_PLANNER_LAYOUT.title
    : 'font-display text-[36px] leading-tight text-blue-900';
  const sectionTitleClassName = isPlannerLayout
    ? SETTLEMENT_PLANNER_LAYOUT.sectionTitle
    : 'text-sm font-semibold text-black mb-3';
  const currentWorkSectionLabel = activeSectionLabel || 'Next Up';
  const showCategoryFilters = categories.length > 0 && (!isPlannerLayout || categories.length > 1);

  // Helper to determine if a card should render based on active filters
  const isItemVisible = (item: PhaseStepState) => {
    if (!activeFilters || activeFilters.length === 0) return true;
    return item.step.category && activeFilters.includes(item.step.category);
  };

  return (
    <div className={containerClassName}>
      <div className={contentColumnClassName}>
      {/* Back arrow + Phase title — Figma: back chevron + 36px serif */}
      <div className={isPlannerLayout ? 'mb-5' : 'mb-2'}>
        {eyebrow && (
          <p className={isPlannerLayout ? SETTLEMENT_PLANNER_LAYOUT.eyebrow : 'text-xs font-semibold uppercase tracking-wide text-blue-600 mb-2'}>{eyebrow}</p>
        )}
        {isPlannerLayout ? (
          <h1 className={titleClassName}>{title}</h1>
        ) : (
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-[35px] h-[35px] flex items-center justify-center text-grey-600 hover:text-blue-900 transition-colors"
              aria-label="Go back"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className={titleClassName}>{title}</h1>
          </div>
        )}
      </div>

      {/* Progress card */}
      <ProgressHeader
        title={title}
        subtitle={subtitle}
        intro={intro}
        variant={isPlannerLayout ? 'planner' : 'default'}
        completedSteps={displayCompletedSteps}
        totalSteps={displayTotalSteps}
        estimatedMinutes={estimatedMinutes}
        completedItemLabel={displayCompletedItemLabel}
      />

      {/* Category filter badges */}
      {showCategoryFilters && (
        <div className="flex flex-wrap gap-3 mb-8 justify-start">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => onCategoryClick?.(cat)}
              className={isPlannerLayout
                ? activeFilters.includes(cat)
                  ? SETTLEMENT_PLANNER_LAYOUT.categoryFilterActive
                  : SETTLEMENT_PLANNER_LAYOUT.categoryFilterInactive
                : `inline-block px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer border-none ${
                    activeFilters.includes(cat)
                      ? 'bg-cyan-600 text-white shadow-sm'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Notification tracker panel (optional) */}
      {notificationTrackerContent && (
        <div className="mb-8">
          {notificationTrackerContent}
        </div>
      )}

      {sectionGroupedSteps ? (
        <div className="space-y-8">
          {sectionGroupedSteps.map((group) => (
            <section key={group.label}>
              <h2 className={isPlannerLayout ? 'text-lg font-medium text-stone-800 mb-4' : 'text-sm font-semibold text-blue-900 mb-1'}>{group.label}</h2>
              <p className={isPlannerLayout ? SETTLEMENT_PLANNER_LAYOUT.sectionDescription : 'text-xs text-grey-600 mb-3'}>
                Work through these steps in order when possible—later filings often depend on earlier court milestones.
              </p>
              <div className="space-y-3">
                {group.steps.map((item) => isItemVisible(item) && (
                  <StepCard
                    key={item.step.id}
                    stepNumber={item.step.stepNumber}
                    title={item.step.title}
                    description={item.step.description}
                    category={item.step.category}
                    status={item.status}
                    subTaskProgress={item.subTaskProgress}
                    appearance={isPlannerLayout ? 'planner' : 'default'}
                    onClick={() => onStepClick(item.step.id)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : sectionOrder === 'next-first' ? (
        usePlannerTripleSections ? (
          <>
            {inProgressItems.length > 0 && (
              <div className="mb-6">
                <h2 className={sectionTitleClassName}>In Progress</h2>
                <div className="space-y-3">
                  {inProgressItems.map((item) => isItemVisible(item) && (
                    <StepCard
                      key={item.step.id}
                      stepNumber={item.step.stepNumber}
                      title={item.step.title}
                      description={item.step.description}
                      category={item.step.category}
                      status={item.status}
                      subTaskProgress={item.subTaskProgress}
                      appearance="planner"
                      onClick={() => onStepClick(item.step.id)}
                    />
                  ))}
                </div>
              </div>
            )}
            {upNextOnlyItems.length > 0 && (
              <div className="mb-6">
                <h2 className={sectionTitleClassName}>Up Next</h2>
                <div className="space-y-3">
                  {upNextOnlyItems.map((item) => isItemVisible(item) && (
                    <StepCard
                      key={item.step.id}
                      stepNumber={item.step.stepNumber}
                      title={item.step.title}
                      description={item.step.description}
                      category={item.step.category}
                      status={item.status}
                      subTaskProgress={item.subTaskProgress}
                      appearance="planner"
                      onClick={() => onStepClick(item.step.id)}
                    />
                  ))}
                </div>
              </div>
            )}
            {completedItems.length > 0 && (
              <div className="mb-6">
                <h2 className={sectionTitleClassName}>Completed</h2>
                <div className="space-y-3">
                  {completedItems.map((item) => isItemVisible(item) && (
                    <StepCard
                      key={item.step.id}
                      stepNumber={item.step.stepNumber}
                      title={item.step.title}
                      description={item.step.description}
                      category={item.step.category}
                      status={item.status}
                      subTaskProgress={item.subTaskProgress}
                      appearance="planner"
                      onClick={() => onStepClick(item.step.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {nextUpItems.length > 0 && (
              <div className="mb-6">
                <h2 className={sectionTitleClassName}>{currentWorkSectionLabel}</h2>
                <div className="space-y-3">
                  {nextUpItems.map((item) => isItemVisible(item) && (
                    <StepCard
                      key={item.step.id}
                      stepNumber={item.step.stepNumber}
                      title={item.step.title}
                      description={item.step.description}
                      category={item.step.category}
                      status={item.status}
                      subTaskProgress={item.subTaskProgress}
                      appearance={isPlannerLayout ? 'planner' : 'default'}
                      onClick={() => onStepClick(item.step.id)}
                    />
                  ))}
                </div>
              </div>
            )}
            {completedItems.length > 0 && (
              <div className="mb-6">
                <h2 className={sectionTitleClassName}>Completed</h2>
                <div className="space-y-3">
                  {completedItems.map((item) => isItemVisible(item) && (
                    <StepCard
                      key={item.step.id}
                      stepNumber={item.step.stepNumber}
                      title={item.step.title}
                      description={item.step.description}
                      category={item.step.category}
                      status={item.status}
                      subTaskProgress={item.subTaskProgress}
                      appearance={isPlannerLayout ? 'planner' : 'default'}
                      onClick={() => onStepClick(item.step.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )
      ) : (
        <>
          {completedItems.length > 0 && (
            <div className="mb-6">
              <h2 className={sectionTitleClassName}>Completed</h2>
              <div className="space-y-3">
                {completedItems.map((item) => isItemVisible(item) && (
                  <StepCard
                    key={item.step.id}
                    stepNumber={item.step.stepNumber}
                    title={item.step.title}
                    description={item.step.description}
                    category={item.step.category}
                    status={item.status}
                    subTaskProgress={item.subTaskProgress}
                    appearance={isPlannerLayout ? 'planner' : 'default'}
                    onClick={() => onStepClick(item.step.id)}
                  />
                ))}
              </div>
            </div>
          )}
          {nextUpItems.length > 0 && (
            <div className="mb-6">
              {(completedItems.length > 0 || activeSectionLabel) && (
                <h2 className={sectionTitleClassName}>{currentWorkSectionLabel}</h2>
              )}
              <div className="space-y-3">
                {nextUpItems.map((item) => isItemVisible(item) && (
                  <StepCard
                    key={item.step.id}
                    stepNumber={item.step.stepNumber}
                    title={item.step.title}
                    description={item.step.description}
                    category={item.step.category}
                    status={item.status}
                    subTaskProgress={item.subTaskProgress}
                    appearance={isPlannerLayout ? 'planner' : 'default'}
                    onClick={() => onStepClick(item.step.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}